/* SiteDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./SiteDetailsViewRenderer");
const AutoAssignmentSummary = require("./veil/AutoAssignSummaryComponent");
const AutoAssignmentPreview = require("./veil/AutoAssignPreviewComponent");

class SiteDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        MUF,
        solutionsManager,
        storageFactory,
        SiteEntity,
        CustomFieldsEntity,
        LicenseRestriction,
        Credentials
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._userSession = userSession;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._storageFactory = storageFactory;
        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._CredentialsEntity = Credentials;
        this._customFieldColumnDef = undefined;
        this._site = undefined;
        this._pathList = {};
        // Site import creates sites one at a time, so debounce to avoid swamping the
        // editor with a full parent reload and form re-render per imported site.
        this._onSiteFeedUpdated = _.debounce(this._onSiteFeedUpdated.bind(this), 1000, {
            maxWait: 5000,
        });
        this._licenseRestriction = LicenseRestriction;
        this._isTopologyAvailable = this._licenseRestriction.checkLicense("TOP");
        this._renderer = new Renderer(this);
        this._SiteEntity = SiteEntity;
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerSiteListeners() {
        this._site.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        this._site.on(
            "PendingPatchesChanged",
            (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this))
        );
        this._site.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _registerSiteFeedListeners() {
        this._siteFeed.on("NewpageEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on("AddEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on("UpdateEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on(
            "InstanceRemoved",
            (this._onInstanceRemovedFromFeed = this._onInstanceRemovedFromFeed.bind(this))
        );
    }

    _unregisterSiteListeners() {
        this._site.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._site.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._site.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unregisterSiteFeedListeners() {
        this._siteFeed.removeListener("NewpageEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("AddEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("UpdateEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("InstanceRemoved", this._onInstanceRemovedFromFeed);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    isSiteCustomField(customFieldDef) {
        return _.get(customFieldDef, "enabled") && !_.get(customFieldDef, "deviceOnly", true);
    }

    get siteCustomFieldIds() {
        return Object.keys(this._customFieldColumnDef || {}).filter((uuid) =>
            this.isSiteCustomField(this._customFieldColumnDef[uuid])
        );
    }

    get customFieldsJtypes() {
        return Object.entries(this._customFieldColumnDef)
            .filter(([, customFieldDef]) => this.isSiteCustomField(customFieldDef))
            .map(([customFieldId, customFieldDef]) =>
                Object.assign(
                    {id: customFieldId},
                    {
                        source: _.get(
                            this._customFieldEntity,
                            `_pristineProperties.fields.${customFieldId}.source`,
                            ""
                        ),
                    },
                    customFieldDef
                )
            )
            .sort(this._CustomFieldsEntityDef.getSortingComparator("order"))
            .map(this.gameEditor.generateCustomFieldJtype);
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    async shutdown() {
        //jshint ignore:line
        // Cancel the debounced handler so it cannot fire after teardown nulls our members
        this._onSiteFeedUpdated.cancel();

        if (!_.isUndefined(this._site)) {
            this._unregisterSiteListeners();
        }

        if (!_.isEmpty(this._siteFeed)) {
            this._unregisterSiteFeedListeners();
            await this._siteFeed.shutdown();
        }

        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._modalUserFeedback = undefined;
                this._userSession = undefined;
                this._CustomFieldsEntityDef = undefined;
                this._storageFactory = undefined;
                this._solutionDefinition = undefined;
                this._renderer = undefined;
                this._pathList = undefined;
                this._solutionName = undefined;
                this._site = undefined;
                this._CredentialsEntity = undefined;
                this._customFieldColumnDef = undefined;
                this._siteNameMap = undefined;
                this._licenseRestriction = undefined;
                this._isTopologyAvailable = undefined;
                this._SiteEntity = undefined;
            },
            (err) => {
                this._logger.warn(err, "SiteDetailsViewComponent.shutdown() error.");
            }
        );
    }

    getCreds(orgId, query = {}) {
        query = Object.assign(query, {
            $and: [{hidden: {$ne: true}}, {credsType: {$eq: "deviceUserPass"}}],
        });
        return this._CredentialsEntity.loadCredentials(orgId, query);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);
    }

    _onPendingPatchesChanged(/*patches, oldPatches*/) {
        return;
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("SitePropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _isValidRegEx(regex) {
        let isValid = true;
        try {
            new RegExp(regex);
        } catch (err) {
            this._logger.warn({error: err}, "SiteDetailsViewComponent._isValidRegEx() - Error.");
            isValid = false;
        }
        return isValid;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        return this._site.isDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenSiteIsReady()
            .then(() => {
                this._site = this._gameEditor.getSite();
                this._isRootSite = this._site._id === this._userSession.activeOrganization.id;
                this._registerSiteListeners();
                return this._SiteEntity
                    .startSitesFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            path: true,
                        },
                        {
                            path: 1,
                        }
                    )
                    .whenInitialized();
            })
            .then((siteFeed) => {
                this._siteFeed = siteFeed;
                this._registerSiteFeedListeners();
                return this.getParents();
            })
            .then(() => {
                return this._CustomFieldsEntityDef.retrieveCustomFields(
                    this._userSession.activeOrganization.id
                );
            })
            .then((customFieldEntity) => {
                this._customFieldEntity = customFieldEntity;
                if (this._site.parentId) {
                    return this._gameEditor._siteEntity.loadSite(
                        this._userSession.activeOrganization.id,
                        this._site.parentId
                    );
                }
                return Promise.resolve(undefined);
            })
            .then((parentSite) => {
                this._parentSite = parentSite;
                return this.loadCustomFieldColumnDefinitions();
            })
            .then(() => {
                // Temporary workaround to prevent editor to go dirty due to missing custom field values
                return this._customFieldSanitizer();
            })
            .then(() => {
                return this.getCreds(this._userSession.activeOrganization.id);
            })
            .then((credEty) => {
                this._credEtys = credEty;
                this.credOptions = this._getCredOptions(credEty);
            })
            .catch((err) => {
                this._logger.warn(err, "SiteDetailsViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This site cannot be opened.",
                    errorCode: "",
                });
                return Promise.reject(err);
            });
    }

    _customFieldSanitizer() {
        const activeCustomFieldIds = Object.keys(this._customFieldColumnDef || {}).filter(
            (uuid) =>
                this._customFieldColumnDef[uuid].systemOwned !== true &&
                this._customFieldColumnDef[uuid].deviceOnly !== true
        );

        let customFieldValues = this._site.customFieldValues
            ? _.cloneDeep(this._site.customFieldValues)
            : {};

        activeCustomFieldIds.forEach((uuid) => {
            if (customFieldValues[uuid] === undefined) {
                if (this._site.customFieldSources[uuid]) {
                    if (_.isNil(_.get(this._parentSite, ["customFieldValues", uuid]))) {
                        customFieldValues[uuid] = null;
                    } else {
                        customFieldValues[uuid] = this._parentSite.customFieldValues[uuid];
                    }
                } else {
                    customFieldValues[uuid] = _.get(
                        this._customFieldColumnDef,
                        [uuid, "default"],
                        null
                    );
                }
            }
        });
        this._site.setCustomFieldValues(customFieldValues);

        if (_.isEmpty(this._site.pendingPatches)) {
            return Promise.resolve();
        } else {
            return this._site
                .update({skipUpdatedOn: true, skipUpdatedBy: true, skipAudit: true})
                .catch((err) => {
                    this._logger.error(
                        {error: err},
                        err,
                        "SiteDetailsViewRenderer: _customFieldSanitizer - Update Error"
                    );
                })
                .finally(() => {
                    return Promise.resolve();
                });
        }
    }

    async getParents() {
        //jshint ignore:line
        const parents = await this._SiteEntity.loadSites(
            this._gameEditor._activeOrgId,
            {path: {$regex: "^(?!" + this._site.getPath() + ")", $options: "i"}},
            {},
            {path: 1}
        );
        const rootSiteId = this._userSession.activeOrganization.id;
        const pathList = {};
        parents.forEach((SiteEntity) => {
            let path = SiteEntity.getPath();
            path = path
                ? path.replace(
                      "/" + this._gameEditor._userSession.activeOrganization.name + "/",
                      "/"
                  )
                : "/";

            if (SiteEntity.getId() === rootSiteId) {
                path = "/";
            }
            Object.assign(pathList, {[`${path}`]: SiteEntity.getId()});
        });
        if (!pathList["/"]) {
            pathList["/"] = rootSiteId;
        }
        this._pathList = pathList;
        const allSites = await this._SiteEntity.loadSites(
            this._gameEditor._activeOrgId,
            {},
            {
                id: true,
                path: true,
                name: true,
            },
            {
                path: 1,
            }
        );
        this._siteNameMap = allSites.reduce((acc, site) => {
            acc[site.id] = site.name;
            return acc;
        }, {});
        return Promise.resolve();
    }

    _onSiteFeedUpdated(/*entity, patches, props, feed*/) {
        return this.getParents().then(() => {
            this.emit("SiteFeedUpdated");
        });
    }

    _onInstanceRemovedFromFeed(instance, cause) {
        if (cause === "Deleted") {
            return this._onSiteFeedUpdated(instance);
        }
    }

    resetSiteProperties() {
        this._site.clearPendingPatches();
        this._site.clearPendingRelationships(); // Case 25550 - clearing relationships as well on cancel and save
        return Promise.resolve();
    }

    async isSiteFormValid(rollbackInvalidData = false) {
        //jshint ignore:line
        const site = this._site;
        const newData = this._site.getProperties();
        const oldData = this._site.getPristineProperties();

        if (this._renderer._formsComponent.isFormInvalid()) {
            throw {
                errorMsg:
                    "Invalid or missing form data present. Please validate that all required entries are correct.",
                errorCode: "InvalidForm",
                title: "Error",
            };
        }

        if (newData.name.trim().length === 0) {
            if (rollbackInvalidData === true) {
                site.setName(oldData.name.trim());
            }
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid Site Name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }

        if (newData.codeName.trim().length === 0) {
            if (rollbackInvalidData === true) {
                site.setCodeName(oldData.codeName.trim());
            }
            throw {
                errorMsg: "Code Name cannot be left empty. Please enter a valid Site Code Name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }

        const codeNameRegExp = new RegExp("^[A-Z0-9-_.]+$");
        if (!codeNameRegExp.test(newData.codeName)) {
            if (rollbackInvalidData === true) {
                site.setCodeName(oldData.codeName.trim());
            }
            throw {
                errorMsg:
                    "Code Name must match the pattern [A-Z0-9-_.]+. Please enter a valid Site Code Name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }

        if (newData.codeName !== oldData.codeName) {
            const sites = await this._SiteEntity.loadSites(this._gameEditor._activeOrgId, {
                codeName: newData.codeName,
            });
            if (sites.length > 0) {
                if (rollbackInvalidData === true) {
                    site.setCodeName(oldData.codeName.trim());
                }
                throw {
                    errorMsg: "Code Name already exists. Please choose a different Site Code Name.",
                    errorCode: "SyntaxError",
                    title: "Error",
                };
            }
        }

        if (
            newData.autoAssignEnabled &&
            (!newData.autoAssignRegex || !this._isValidRegEx(newData.autoAssignRegex))
        ) {
            if (rollbackInvalidData === true) {
                site.setRegexp(oldData.autoAssignRegex.trim());
            }
            throw {
                errorMsg:
                    "Oops… you’ll need to enter a valid regex string to automatically assign devices to the site.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }
    }

    saveSite() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._site.isDirty()) {
                    return this.isSiteFormValid().then(() => {
                        if (this._site.credsName !== this._site.pristineProperties.credsName) {
                            this._site.setCredsByName(this._site.credsName);
                        }
                        if (
                            this._isTopologyAvailable &&
                            (this._site.pristineProperties.discoveryLevel !==
                                this._site.properties.discoveryLevel ||
                                this._site.pristineProperties.discoveryLevelFrom !==
                                    this._site.properties.discoveryLevelFrom) &&
                            this._site.properties.discoveryLevel < 3
                        ) {
                            return this._modalUserFeedback
                                .displayConfirmation(
                                    "You have a Topology license for this organization. Set the Discovery Level to 3 for best results. Levels 1 and 2 do not provide sufficient information for Topology.",
                                    {
                                        notDismissable: true,
                                        title: "Discovery Level",
                                        confirmDetails: {
                                            text: "OK",
                                            type: "additive",
                                        },
                                        rejectDetails: {
                                            text: "Cancel",
                                            type: "text",
                                        },
                                    }
                                )
                                .then(() => {
                                    return this._site.update();
                                });
                        }
                        return this._site.update();
                    });
                }
            })
            .catch((err) => {
                this._logger.warn({error: err}, "SiteDetailsViewComponent.saveSite() error.");
                if (err.userRejected) {
                    return Promise.reject();
                }
                return this.displayError(err);
            });
    }

    displayError(err) {
        if (err && err.errorCode === "NoPendingUpdates") {
            this.emit("DevicePropertiesChanged");
            return Promise.reject();
        } else {
            if (err && err.errorMsg && err.errorMsg.includes("The Site already exists")) {
                err.errorMsg =
                    "This site name or code already exists. " +
                    "Sites that have the same parent must have unique names. " +
                    "Site codes must be unique.";
            }
            return this._modalUserFeedback.hideDisplayed().then(() => {
                if (!err.userRejected) {
                    this._modalUserFeedback.displayError(err);
                }
            });
        }
    }

    updateRelationships() {
        return Promise.resolve()
            .then(() => {
                // Do nothing unless this guy is dirty
                if (this._site.isDirty()) {
                    return this._site.updateRelationships();
                }
            })
            .then(() => {
                if (!this.isDirty()) {
                    this._gameEditor.clearOnBeforeUnload();
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "SiteDetailsViewComponent.updateRelationships() error."
                );
                if (err.errorCode === "NoPendingUpdates") {
                    return Promise.reject();
                } else {
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    async _previewAutoAssignSites() {
        //jshint ignore:line
        // if we provide any additional options such as preview, we must make a complete call
        let previewWorkInput = {
            preview: true,
            siteId: this._site._id,
            autoAssignProperty: this._site.autoAssignProperty,
            autoAssignRegex: this._site.autoAssignRegex,
        };

        if (this._autoAssignUnchecked) {
            previewWorkInput.autoAssignEnabled = false;
        }

        let results = await this._site.runAutoAssignSitesWork(previewWorkInput, {});

        try {
            await this._modalUserFeedback.displayCustom(
                new AutoAssignmentPreview(results, this._logger)
            );
        } catch (error) {
            if (!error.userRejected) {
                this._logger.warn(
                    {error},
                    "SiteDetailsViewComponent._previewAutoAssignSites() error"
                );
            }
            return false;
        }

        return true;
    }

    _autoAssignSites() {
        const progressFn = () => {};
        return this._site.runAutoAssignSitesWork({}, {}, progressFn).then((result) => {
            return this._modalUserFeedback.displayCustom(
                new AutoAssignmentSummary(result, this._logger)
            );
        });
    }

    loadCustomFieldColumnDefinitions() {
        return this._CustomFieldsEntityDef
            .loadColumnDefinitions(this._userSession.activeOrganization.id)
            .then((customFieldColumnDef) => {
                this._customFieldColumnDef = customFieldColumnDef;
                return Promise.resolve();
            });
    }

    _getCredOptions(credEtys) {
        let opts = {};
        if (this._site.parentId) {
            // if (this._site.credsName && this._site.credsName.search("_Site") >= 0) {
            //     const parentSiteCredsName = _.get(this, "_parentSite.credsName");
            //     if (this._site.credsName === "_Site" && parentSiteCredsName) {
            //         if (parentSiteCredsName.search("_Site") >= 0) {
            //             opts[parentSiteCredsName] = parentSiteCredsName;
            //         } else {
            //             opts[`_Site(${parentSiteCredsName})`] = `_Site(${parentSiteCredsName})`;
            //         }
            //     } else {
            //         opts[this._site.credsName] = this._site.credsName;
            //     }
            // }
            if (this._site.credsName && this._site.credsName.search("_Site") >= 0) {
                opts[this._site.credsName] = this._site.credsName;
            } else {
                opts["_Site"] = "_Site";
            }
        }

        credEtys.forEach((cred) => {
            opts[cred.name] = cred.id;
        });
        return opts;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = SiteDetailsViewComponent;

SiteDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.entities.Site",
    "$teve.entities.CustomFields",
    "$teve.licenseRestriction",
    "$teve.entities.Credentials",
];

SiteDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Site Details",
    icon: "siteexplorer.site_details",
    shown: true,
    title: "Details",
    order: 1,
};
